/**
 * solicitudes.ts — Web data-layer for solicitud_usuario approval flow.
 *
 * Only the cambio_unidad path is wired; cambio_lineas is DEFERRED (DEFERRED-A7/F1).
 *
 * Badge derivation helper: a section is "pending" when there is at least one
 * solicitud with estado IN ('nueva', 'en_revision') for the given tipo.
 * NOTE: EstadoSolicitud has NO 'pendiente' value — never check for that string.
 */

import { apiClient, apiGet } from '@/lib/apiClient';
import { errorDeRespuesta } from '@/lib/api-error-message';
import {
  AdscripcionContextoResponseSchema,
  SolicitudResponseSchema,
  SolicitudesListResponseSchema,
  UnidadesCatalogoResponseSchema,
  type AdscripcionContextoResponse,
  type CreateSolicitudInput,
  type SolicitudResponse,
  type SolicitudesListResponse,
  type UnidadesCatalogoResponse,
  type UpdateSolicitudEstadoInput,
} from '@siiv/schemas';

const PENDING_ESTADOS = new Set(['nueva', 'en_revision']);

/**
 * Returns true when the given list of solicitudes contains at least one
 * pending entry for the specified tipoSolicitud.
 *
 * Usage:
 *   const pending = hasPendingSolicitud(solicitudes, 'cambio_unidad');
 */
export function hasPendingSolicitud(
  solicitudes: SolicitudResponse[],
  tipoSolicitud: string,
): boolean {
  return solicitudes.some(
    (s) => s.tipoSolicitud === tipoSolicitud && PENDING_ESTADOS.has(s.estado),
  );
}

/**
 * POST /solicitudes — create a cambio_unidad solicitud (combined adscription state, R65).
 * Returns the parsed SolicitudResponse (201). A refusal throws an `ApiRequestError` carrying the
 * server's Spanish message (cap, repeated unit, no change, ...).
 */
export async function createSolicitud(
  input: CreateSolicitudInput,
): Promise<SolicitudResponse> {
  const res = await apiClient('/solicitudes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!res.ok) {
    throw await errorDeRespuesta(res, 'No se pudo enviar la solicitud. Reintenta en unos segundos.');
  }

  const json = await res.json();
  return SolicitudResponseSchema.parse(json);
}

/**
 * GET /investigadores/:slug/adscripcion — the current principal + additional units, the held lines that
 * a unit change could close and the pending request, for the request form, its live preview and the admin
 * resolution panel. Owner or admin only.
 */
export async function getAdscripcionContexto(slug: string): Promise<AdscripcionContextoResponse> {
  const json = await apiGet<unknown>(`/investigadores/${encodeURIComponent(slug)}/adscripcion`);
  return AdscripcionContextoResponseSchema.parse(json);
}

/**
 * GET /solicitudes/me — list the authenticated user's solicitudes.
 */
export async function getMisSolicitudes(): Promise<SolicitudesListResponse> {
  const json = await apiGet<unknown>('/solicitudes/me');
  return SolicitudesListResponseSchema.parse(json);
}

/**
 * GET /solicitudes/investigador/:slug — admin only: the full history of ONE
 * researcher, for the read-only view the admin opens from the profile action bar
 * and from the user detail. Throws if the caller is not admin (403).
 */
export async function getSolicitudesDeInvestigador(
  slug: string,
): Promise<SolicitudesListResponse> {
  const json = await apiGet<unknown>(
    `/solicitudes/investigador/${encodeURIComponent(slug)}`,
  );
  return SolicitudesListResponseSchema.parse(json);
}

/**
 * GET /investigadores/catalogos/unidades — unit catalog for the "cambio de unidad"
 * solicitud form (target unit picker).
 */
export async function getUnidadesCatalogo(): Promise<UnidadesCatalogoResponse> {
  const json = await apiGet<unknown>('/investigadores/catalogos/unidades');
  return UnidadesCatalogoResponseSchema.parse(json);
}

/**
 * GET /solicitudes — admin only: list pending solicitudes (nueva|en_revision).
 * Throws if the caller is not admin (403).
 */
export async function getSolicitudesPendientes(): Promise<SolicitudesListResponse> {
  const json = await apiGet<unknown>('/solicitudes');
  return SolicitudesListResponseSchema.parse(json);
}

/**
 * GET /solicitudes/all — admin only: full dashboard list, optionally filtered
 * by estado. Throws if the caller is not admin (403).
 */
export async function getSolicitudesAdmin(
  estado?: string,
): Promise<SolicitudesListResponse> {
  const qs = estado && estado !== 'todas' ? `?estado=${encodeURIComponent(estado)}` : '';
  const json = await apiGet<unknown>(`/solicitudes/all${qs}`);
  return SolicitudesListResponseSchema.parse(json);
}

/**
 * PATCH /solicitudes/:id — admin only: resolve (resuelta) or reject (rechazada).
 * On approval of a cambio_unidad the backend applies the unit change; `decisiones` approves or rejects
 * each item of a combined adscription request (R65). A refusal throws an `ApiRequestError`.
 */
export async function updateSolicitudEstado(
  idSolicitud: number,
  input: UpdateSolicitudEstadoInput,
): Promise<SolicitudResponse> {
  const res = await apiClient(`/solicitudes/${idSolicitud}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!res.ok) {
    // 400 (cap / "Otra" after recompute: "... Rechaza algunos ítems de la solicitud para continuar.") and 409
    // ("La solicitud ya fue resuelta...") arrive with their own Spanish text, which the admin screen shows.
    throw await errorDeRespuesta(res, 'No se pudo resolver la solicitud. Intenta de nuevo.');
  }

  const json = await res.json();
  return SolicitudResponseSchema.parse(json);
}
